import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Search, ExternalLink, Copy, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { polymarketApi, type Market, type OrderBook } from '@/api/polymarket';
import { DATE_LOCALE } from '@/lib/utils';

interface Trade {
  proxyWallet: string;
  side: string;
  asset: string;
  conditionId: string;
  size: number;
  price: number;
  timestamp: number;
  transactionHash: string;
  title: string;
  name: string;
  outcome: string;
}

interface SearchParams {
  slug?: string;
}

export const Route = createFileRoute('/custom/trades')({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    slug: typeof search.slug === 'string' ? search.slug : undefined
  }),
  component: TradesExplorerPage
});

function extractSlug(input: string): string {
  const match = input.match(/https?:\/\/(?:www\.)?polymarket\.com\/event\/([^/?]+)/i);
  return match?.[1] ?? input.trim();
}

function TradesExplorerPage() {
  const navigate = useNavigate();
  const { slug } = Route.useSearch();

  const [searchTerm, setSearchTerm] = useState(slug ?? '');
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_orderBookYes, setOrderBookYes] = useState<OrderBook | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_orderBookNo, setOrderBookNo] = useState<OrderBook | null>(null);
  const [shares, setShares] = useState('100');
  const [limitPrice, setLimitPrice] = useState('0.97');
  const [avgPnlYes, setAvgPnlYes] = useState<number | null>(null);
  const [avgPnlNo, setAvgPnlNo] = useState<number | null>(null);
  const [holderCountYes, setHolderCountYes] = useState(0);
  const [holderCountNo, setHolderCountNo] = useState(0);
  const [loadingPnl, setLoadingPnl] = useState(false);
  const [error, setError] = useState('');
  const isUpdatingUrl = useRef(false);

  const searchMutation = useMutation({
    mutationFn: (query: string) => polymarketApi.searchEvents(query),
    onSuccess: data => {
      if (data.success && data.data) {
        setSelectedMarket(null);
        setTrades([]);
      }
    },
    onError: (err: Error) => setError(err.message)
  });

  const markets = searchMutation.data?.data ?? [];

  useEffect(() => {
    if (slug && !isUpdatingUrl.current && slug !== searchTerm) {
      setSearchTerm(slug);
      searchMutation.mutate(slug);
    }
    isUpdatingUrl.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleSearch = () => {
    if (!searchTerm) {
      return;
    }
    setError('');
    searchMutation.mutate(searchTerm);
    isUpdatingUrl.current = true;
    navigate({ to: '/custom/trades', search: { slug: extractSlug(searchTerm) } });
  };

  const fetchTrades = async (conditionId: string) => {
    try {
      const response = await fetch(`https://data-api.polymarket.com/trades?market=${conditionId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch trades: ${response.statusText}`);
      }
      setTrades(await response.json());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch trades');
    }
  };

  const fetchOrderBook = async (tokenId: string, side: 'yes' | 'no') => {
    try {
      const result = await polymarketApi.getOrderBook(tokenId);
      if (result.success && result.data) {
        (side === 'yes' ? setOrderBookYes : setOrderBookNo)(result.data);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch order book');
    }
  };

  const fetchAveragePnl = async (conditionId: string) => {
    setLoadingPnl(true);
    setAvgPnlYes(null);
    setAvgPnlNo(null);
    setHolderCountYes(0);
    setHolderCountNo(0);

    try {
      const res = await fetch(`https://data-api.polymarket.com/holders?market=${conditionId}&limit=20`);
      if (!res.ok) {
        throw new Error('Failed to fetch holders');
      }
      const holdersData = await res.json();

      const yesHolders: string[] = [];
      const noHolders: string[] = [];
      const holdersArray = Array.isArray(holdersData) ? holdersData : [holdersData];

      holdersArray.forEach((item: { holders?: Array<{ proxyWallet?: string; outcomeIndex?: number }> }) => {
        item.holders?.forEach(holder => {
          if (holder.proxyWallet) {
            if (holder.outcomeIndex === 0) {
              yesHolders.push(holder.proxyWallet);
            } else if (holder.outcomeIndex === 1) {
              noHolders.push(holder.proxyWallet);
            }
          }
        });
      });

      const fetchPnl = async (wallet: string): Promise<number | null> => {
        try {
          const r = await fetch(`https://data-api.polymarket.com/v1/leaderboard?user=${wallet}&timePeriod=ALL&limit=1`);
          if (!r.ok) {
            return null;
          }
          const d = await r.json();
          return (Array.isArray(d) ? d[0] : d)?.pnl ?? null;
        } catch {
          return null;
        }
      };

      const [yesPnls, noPnls] = await Promise.all([
        Promise.all(yesHolders.map(fetchPnl)),
        Promise.all(noHolders.map(fetchPnl))
      ]);

      const validYes = yesPnls.filter((p): p is number => p !== null);
      const validNo = noPnls.filter((p): p is number => p !== null);

      if (validYes.length > 0) {
        setAvgPnlYes(validYes.reduce((s, p) => s + p, 0) / validYes.length);
        setHolderCountYes(validYes.length);
      }
      if (validNo.length > 0) {
        setAvgPnlNo(validNo.reduce((s, p) => s + p, 0) / validNo.length);
        setHolderCountNo(validNo.length);
      }
    } catch {
      // silently fail
    } finally {
      setLoadingPnl(false);
    }
  };

  const handleMarketSelect = (market: Market) => {
    setSelectedMarket(market);
    setOrderBookYes(null);
    setOrderBookNo(null);
    setAvgPnlYes(null);
    setAvgPnlNo(null);
    setHolderCountYes(0);
    setHolderCountNo(0);
    fetchTrades(market.conditionId);
    fetchAveragePnl(market.conditionId);

    if (market.clobTokenIds) {
      try {
        const tokenIds = JSON.parse(market.clobTokenIds);
        if (tokenIds?.[0]) {
          fetchOrderBook(tokenIds[0], 'yes');
        }
        if (tokenIds?.[1]) {
          fetchOrderBook(tokenIds[1], 'no');
        }
      } catch {
        /* ignore */
      }
    }
  };

  const placeOrderMutation = useMutation({
    mutationFn: polymarketApi.placeOrder,
    onSuccess: () => alert('Order placed successfully!'),
    onError: (err: Error) => setError(err.message)
  });

  const getOutcomeNames = (m: Market | null): [string, string] => {
    if (!m?.outcomes) {
      return ['Yes', 'No'];
    }
    try {
      const o = typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : m.outcomes;
      if (Array.isArray(o) && o.length >= 2) {
        return [String(o[0]), String(o[1])];
      }
    } catch {
      /* ignore */
    }
    return ['Yes', 'No'];
  };

  const [outcomeName1, outcomeName2] = getOutcomeNames(selectedMarket);

  const handlePlaceOrder = (outcomeIndex: 0 | 1) => {
    if (!selectedMarket?.clobTokenIds) {
      setError('Select a market first');
      return;
    }
    const sharesNum = parseFloat(shares);
    const priceNum = parseFloat(limitPrice);
    if (isNaN(sharesNum) || sharesNum <= 0) {
      setError('Invalid shares');
      return;
    }
    if (isNaN(priceNum) || priceNum <= 0 || priceNum > 1) {
      setError('Price must be between 0 and 1');
      return;
    }

    const tokenIds = JSON.parse(selectedMarket.clobTokenIds);
    const tokenId = tokenIds[outcomeIndex];
    if (!tokenId) {
      setError('Token ID not found');
      return;
    }

    placeOrderMutation.mutate({
      tokenId,
      side: 'BUY',
      size: sharesNum,
      price: priceNum,
      negRisk: selectedMarket.negRisk,
      question: selectedMarket.question,
      outcome: outcomeIndex === 0 ? outcomeName1 : outcomeName2
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Trades Explorer</h1>
        <p className="text-sm text-muted-foreground">Search Polymarket events and explore trades</p>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1 max-w-xl space-y-2">
          <Label>Event Slug or URL</Label>
          <div className="flex gap-2">
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="e.g. maduro-out-in-2025 or https://polymarket.com/event/..."
            />
            <Button onClick={handleSearch} disabled={searchMutation.isPending || !searchTerm}>
              {searchMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      {markets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Select a Market</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {markets.map(market => (
                <button
                  key={market.id}
                  onClick={() => handleMarketSelect(market)}
                  className={`flex flex-col items-start rounded-md border p-3 text-left transition-colors ${
                    selectedMarket?.id === market.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                  }`}
                >
                  <span className="line-clamp-2 text-sm font-medium">{market.question}</span>
                  <span className="mt-1 font-mono text-xs text-muted-foreground">
                    {market.conditionId.slice(0, 12)}...
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedMarket && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{selectedMarket.question}</CardTitle>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono">{selectedMarket.conditionId}</span>
              <button onClick={() => navigator.clipboard.writeText(selectedMarket.conditionId)}>
                <Copy className="size-3" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <p className="mb-3 text-sm font-medium">Place Order</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Shares</Label>
                    <Input type="number" value={shares} onChange={e => setShares(e.target.value)} min="1" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Limit Price</Label>
                    <Input
                      type="number"
                      value={limitPrice}
                      onChange={e => setLimitPrice(e.target.value)}
                      min="0"
                      max="1"
                      step="0.01"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      className="w-full"
                      onClick={() => handlePlaceOrder(0)}
                      disabled={placeOrderMutation.isPending}
                    >
                      Buy {outcomeName1}
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => handlePlaceOrder(1)}
                      disabled={placeOrderMutation.isPending}
                    >
                      Buy {outcomeName2}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground">Volume</p>
                <p className="text-lg font-semibold">
                  {selectedMarket.volume ? `$${Math.round(selectedMarket.volume).toLocaleString()}` : 'N/A'}
                </p>
              </div>
              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground">{outcomeName1} Price</p>
                <p className="text-lg font-semibold text-green-500">
                  {selectedMarket.bestAsk ? `${(selectedMarket.bestAsk * 100).toFixed(1)}¢` : 'N/A'}
                </p>
              </div>
              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground">{outcomeName2} Price</p>
                <p className="text-lg font-semibold text-red-500">
                  {selectedMarket.bestBid ? `${((1 - selectedMarket.bestBid) * 100).toFixed(1)}¢` : 'N/A'}
                </p>
              </div>
              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground">Token IDs</p>
                {selectedMarket.clobTokenIds ? (
                  (() => {
                    try {
                      const ids = JSON.parse(selectedMarket.clobTokenIds!);
                      return (
                        <div className="space-y-0.5 font-mono text-[10px]">
                          <p>
                            {outcomeName1}: {ids[0]?.slice(0, 12)}...
                          </p>
                          <p>
                            {outcomeName2}: {ids[1]?.slice(0, 12)}...
                          </p>
                        </div>
                      );
                    } catch {
                      return <p className="text-xs text-muted-foreground">Invalid</p>;
                    }
                  })()
                ) : (
                  <p className="text-xs text-muted-foreground">N/A</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground">
                  {outcomeName1}: Avg P&L {holderCountYes > 0 && `(Top ${holderCountYes})`}
                </p>
                <p className="text-lg font-semibold">
                  {loadingPnl ? (
                    <Loader2 className="inline size-4 animate-spin" />
                  ) : avgPnlYes !== null ? (
                    <span className={avgPnlYes >= 0 ? 'text-green-500' : 'text-red-500'}>
                      ${avgPnlYes >= 0 ? '+' : ''}
                      {avgPnlYes.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground">
                  {outcomeName2}: Avg P&L {holderCountNo > 0 && `(Top ${holderCountNo})`}
                </p>
                <p className="text-lg font-semibold">
                  {loadingPnl ? (
                    <Loader2 className="inline size-4 animate-spin" />
                  ) : avgPnlNo !== null ? (
                    <span className={avgPnlNo >= 0 ? 'text-green-500' : 'text-red-500'}>
                      ${avgPnlNo >= 0 ? '+' : ''}
                      {avgPnlNo.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Side</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Potential</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Tx Hash</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trades.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    {searchMutation.isPending
                      ? 'Loading...'
                      : selectedMarket
                        ? 'No trades found.'
                        : 'Search for an event and select a market.'}
                  </TableCell>
                </TableRow>
              ) : (
                trades.map((trade, i) => (
                  <TableRow key={`${trade.transactionHash}-${i}`}>
                    <TableCell className="max-w-[150px] truncate">{trade.name}</TableCell>
                    <TableCell>
                      <Badge variant={trade.side === 'BUY' ? 'default' : 'secondary'}>{trade.side}</Badge>
                    </TableCell>
                    <TableCell>{trade.outcome}</TableCell>
                    <TableCell className="font-mono">{trade.price}</TableCell>
                    <TableCell className="font-mono">{trade.size}</TableCell>
                    <TableCell className="font-mono">
                      {trade.side === 'BUY' ? (
                        <span className="text-green-500">+${(trade.size * (1 - trade.price)).toFixed(2)}</span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {new Date(trade.timestamp * 1000).toLocaleString(DATE_LOCALE)}
                    </TableCell>
                    <TableCell>
                      <a
                        href={`https://polygonscan.com/tx/${trade.transactionHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
                      >
                        {trade.transactionHash.slice(0, 8)}...
                        <ExternalLink className="size-3" />
                      </a>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
